import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import {
  Observable,
  catchError,
  throwError,
  BehaviorSubject,
  filter,
  take,
  switchMap,
} from 'rxjs';
import { ToastService } from '../_metronic/shared/components/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { getErrorMessage } from '../_metronic/shared/utils/helper';
import { LocalStorageService } from 'ngx-webstorage';
import { AuthService } from '../services/auth/auth.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly localStorageService: LocalStorageService,
    private readonly injector: Injector
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err.status === 401) {
          // Jika endpoint yang gagal adalah refresh-token itu sendiri, langsung logout
          if (request.url.includes('auth/refresh-token')) {
            this.handleLogout();
            return throwError(() => err);
          }

          const loginType = this.localStorageService.retrieve('login_type');
          if (loginType === 'form') {
            return this.handle401Error(request, next);
          } else {
            this.handleLogout();
            return throwError(() => err);
          }
        }

        const errorMessage = err.error?.response_schema?.response_message
          ? getErrorMessage(err, this.translate)
          : `Error: ${err.message ?? err.status}`;

        if (err.status >= 500) {
          this.toast.error(errorMessage);
        }
        return throwError(() => err);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const authService = this.injector.get(AuthService);
      return authService.refreshToken().pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          const newToken = res?.response_output?.detail?.access_token;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.handleLogout();
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => !!token),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleLogout() {
    this.localStorageService.clear();
    window.location.href = window.location.origin + '/#/login';
  }
}
